/* eslint-disable max-lines,max-statements,no-console */
import debugFactory from "debug";
import get from "lodash/get";
import orderBy from "lodash/orderBy";
import { DateTime } from "luxon";
import moment from "moment-timezone";
import { validate as validateUuid } from "uuid";

import {
	generateZoomAuthUrl,
	luxonext
} from "@teaming/common-util";

import storage from "@teaming/common-storage";

import {
	DiscColor,
	PopoverVisibility,
	StreamDocumentType,
	TeamMemberStatusType,
	TeamType,
	TemplateCategory,
	TemplateScope,
	TopicCategory,
	TranscriptionJobStatus,
	TranscriptionOrigin
} from "@teaming/common-enums";

import { alertFactory } from "@teaming/common-ui";

import {
	defaultSuggestedTemplateTypes,
	flattenTemplateTopics,
	teamingTemplateFactory as templateFactory,
	templateOrder,
	templateSelectionTimestamp
} from "@teaming/common-templates";

import {
	makeMarkMap,
	streamNoteLink
} from "@teaming/common-team";

import { configureChannels, configureSockets } from "./listeners";

import {
	pristineRecurrenceInfo,
	pristineState,
	pristineTopicCategory,
	pristineTranscription,
	pristineTranscriptionSummary
} from "./state";

import {
	newOneTimeMeetingForm,
	newRecurrenceForm,
	newTemplateForm,
	// FIXME: use this thang
	oneTimeMeetingFromFields,
	oneTimeMeetingToFields,
	recurrenceFromFields,
	recurrenceToFields
} from "./forms";

import { v4 } from "uuid";
import {
	computeNoteHistory,
	computeRecommendedTemplates
} from "./helpers";

const LUXON_DATETIME_FORMAT = "yyyy-LL-dd HH:mm:ss"; // 2023-01-01 16:00:00
const LUXON_SHORTDATE_FORMAT = "L/d/yyyy"; // 1/15/2023

export default function streamStoreFactory( {
	storeName,
	foundation,
	appChannels,
} ) {
	const debug = debugFactory( `teaming:common-team:stores:${ storeName }` );

	const { store, navigation, i18n, ioSocket, ioMeeting, ioTeam, ioStream, ioUser, telemetry, config, } = foundation();
	const { t, } = i18n.global;
	const { ioChannel, } = appChannels;

	const markMap = makeMarkMap( i18n );

	const getUserProfile = () => store.getters[ "host-userProfile/userProfile" ];

	const { success, error, info, warn, } = alertFactory( "common-team.stores.Stream", t );
	const alerts = { success, error, info, warn, };

	configureSockets( {
		storeName,
		store,
		navigation,
		ioSocket,
		ioTeam,
		ioStream,
		alerts,
	} );

	configureChannels( {
		storeName,
		store,
		navigation,
		ioChannel,
		ioMeeting,
		alerts,
	} );

	const translate = ( key, args ) => t( `common-team.stores.Stream.${ key }`, args );

	const translateTemplateCategory = templateCategory => translate( `templateCategory${ templateCategory }label` );

	const defaultNoteTitle = subDoc => translate( "defaultNoteTitle", {
		date: DateTime.fromFormat( subDoc.date, LUXON_DATETIME_FORMAT ).toFormat( "DD" ),
	} );

	const getTeamTelemetry = team => ( team?.type === TeamType.TEAM ? telemetry.team : telemetry.oneonone );

	const myLeadTeamIds = myTeams => {
		const userProfile = getUserProfile();
		return myTeams.filter( myTeam => myTeam.isLeadMember( userProfile.personId ) )
			.map( myTeam => myTeam.id );
	};

	/**
	 * param [Template] templates
	 * param {meetingTemplateId:timestamp} selectedTemplates
	 */
	const extendTemplates = ( templates, selectedTemplates = {}, myTeams = [] ) => {
		const userProfile = getUserProfile();
		const currentUserId = userProfile.personId;
		const leadTeamIds = myLeadTeamIds( myTeams );

		return templates.map( template => {
			const { teamId, isTeamingTemplate, } = template;

			let category, type, canDelete;
			const isCustomTemplate = !isTeamingTemplate;

			if ( isCustomTemplate ) {
				canDelete = ( template.createdBy === currentUserId ) ||
					leadTeamIds.includes( teamId );
				category = teamId ? teamId : TemplateCategory.MY_TEMPLATES;
				type = "CUSTOM";
			} else {
				canDelete = false;
				category = template.scope === TemplateScope.TEAM ?
					TemplateCategory.TEAM_TEMPLATES :
					TemplateCategory.ONE_ON_ONE_TEMPLATES;
				type = template.type;
			}

			const lastAddedTimestamp = selectedTemplates[ template.meetingTemplateId ] || "";
			let lastAdded = "";
			let lastAddedAt = 0;
			if ( lastAddedTimestamp ) {
				const lastAddedLuxon = templateSelectionTimestamp.parse( lastAddedTimestamp );
				lastAdded = lastAddedLuxon.toFormat( LUXON_SHORTDATE_FORMAT );
				lastAddedAt = lastAddedLuxon.toUnixInteger();
			}
			const createdAt = DateTime.fromISO( template.created, { zone: "UTC", } )
				.toUnixInteger();

			return {
				...template,
				canDelete,
				category,
				createdAt,
				isCustomTemplate,
				lastAdded,
				lastAddedAt,
				topics: flattenTemplateTopics( template ),
				type,
			};
		} );
	};

	/**
	 * Map meetings with template name and sort by date
	 */
	const mapMeetings = ( { meetings, templates, } ) => {
		const templateMap = templates.reduce( ( map, template ) => {
			map[ template.meetingTemplateId ] = template;
			return map;
		}, {} );

		const mappedMeetings = meetings.map( meeting => {
			const { meetingTemplateId, } = meeting;
			const template = templateMap[ meetingTemplateId ];
			meeting.meetingTemplateName = template?.name || "";
			const sortKey = DateTime.fromISO( meeting.createdAt, { zone: "utc", } )
				.toUnixInteger();
			return {
				...meeting,
				sortKey,
			};
		} );

		return orderBy( mappedMeetings, [ "sortKey", ], [ "desc", ] );
	};

	const transcriptionBotRetries = {};

	return {
		namespaced: true,
		state: pristineState( i18n ),
		getters: {
			allowedTeamingActions( state ) {
				const { currentDocType, } = state;
				if ( currentDocType === StreamDocumentType.PRIVATE ) {
					return [ "topic", ];
				}
				return [ "topic", "commitment", "decision", ];
			},
			busy: state => state.busy,
			bypassTranscriptionBotPrompt: ( state, getters ) => !getters.userProfile.settings.showTranscriptionBotPrompt,
			bypassTranscriptionUploadPrompt: ( state, getters ) => !getters.userProfile.settings.showTranscriptionInterstitial,
			context( state, getters ) {
				const userProfile = getUserProfile();
				const { currentTeam, cursorColor, } = getters;
				const { id: teamId = "", members = [], } = currentTeam;
				return {
					name: userProfile.name,
					personId: userProfile.personId,
					// token: this.token,
					color: cursorColor,
					teamId,
					teamMembers: [ ...members, ].filter( m => m.status === TeamMemberStatusType.ACTIVE ),
				};
			},
			currentDocType: state => state.currentDocType,
			currentTeam: state => state.currentTeam,
			currentTeamId: state => state.currentTeamId,
			cursorColor() {
				const userProfile = getUserProfile();
				const discHighType = userProfile?.assessments?.DISC?.highType || "";
				return discHighType ?
					DiscColor[ discHighType ] :
					"#A3ACB9"; // gray / $color1-300
			},
			cursors: state => {
				return state.cursors[ state.currentDocType ];
			},
			customTemplates( state ) {
				const { customTemplates, selectedTemplates, myTeams, } = state;
				return extendTemplates( customTemplates, selectedTemplates, myTeams );
			},
			/** default suggested templates for the current team type */
			defaultTeamTypeSuggestedTemplates( state, getters ) {
				const { currentTeam, suggestedTemplates, } = getters;
				const suggestedTemplateMap = suggestedTemplates.reduce( ( map, template ) => {
					map[ template.type ] = template;
					return map;
				}, {} );
				const suggestedTemplateTypes = defaultSuggestedTemplateTypes[ currentTeam?.type ] || [];
				return suggestedTemplateTypes.map( templateType => suggestedTemplateMap[ templateType ] );
			},
			documentName: state => state.documentName,
			/** templates filtered by selected template category */
			filteredTemplates( state, getters ) {
				const {
					selectedTemplateCategory,
					suggestedTemplates,
					customTemplates,
					userProfile,
				} = getters;

				// teaming templates are already sorted and only need to be filtered
				if ( [
					TemplateCategory.TEAM_TEMPLATES,
					TemplateCategory.ONE_ON_ONE_TEMPLATES,
				].includes( selectedTemplateCategory ) ) {
					return suggestedTemplates.filter( template => template.category === selectedTemplateCategory );
				}

				// custom templates need to be filtered *and* sorted by creation date
				const filteredCustomTemplates = customTemplates.filter( template => {
					if ( selectedTemplateCategory === TemplateCategory.MY_TEMPLATES ) {
						return template.isCustomTemplate &&
							( template.createdBy === userProfile.personId ) &&
							!template.teamId;
					}
					return template.category === selectedTemplateCategory;
				} );

				return orderBy( filteredCustomTemplates, [ "createdAt", ], [ "desc", ] );
			},
			haveSubDocsSynced: state => state.subDocSyncCycle > 0,
			isBusy: state => {
				const { busy, } = state;
				return Object.keys( busy ).reduce( ( acc, key ) => acc || busy[ key ], false );
			},
			isInitialized: state => state.isInitialized,
			isNavigationSidebarExpanded: state => state.isNavigationSidebarExpanded,
			isPrivateStream( state ) {
				return state.currentDocType === StreamDocumentType.PRIVATE;
			},
			recurrenceListVisibility: state => state.recurrenceListVisibility,
			isSharedStream( state ) {
				return state.currentDocType === StreamDocumentType.SHARED;
			},
			isTemplateSaveConfirmationVisible: state => state.isTemplateSaveConfirmationVisible,
			isTranscriptDialogVisible: state => !!state.transcriptionViewedSubDocId,
			isTranscriptionBotPromptVisible: state => state.isTranscriptionBotPromptVisible,
			isTranscriptionUploadPromptVisible: state => state.isTranscriptionUploadPromptVisible,
			isZoomAuthorized() {
				if ( !config.enableZoomOauth ) {
					return true;
				}
				const { recallZoomAuthCredentialUuid, } = getUserProfile();
				return recallZoomAuthCredentialUuid !== null && validateUuid( recallZoomAuthCredentialUuid );
			},
			/** template object constructed from the template form */
			newMaterializedTemplate( state, getters ) {
				const { myTeams, } = state;
				const { newTemplateForm: form, } = getters;
				const { meetingTemplateId, name, description, content, category, } = form;

				let teamId = null;
				let scope = TemplateScope.GLOBAL;
				if ( category !== TemplateCategory.MY_TEMPLATES ) {
					teamId = category;
					const templateTeam = myTeams.find( myTeam => myTeam.id === teamId );
					scope = templateTeam.getTemplateScope();
				}

				return {
					meetingTemplateId,
					name,
					description,
					content,
					scope,
					teamId,
				};
			},
			newTemplateForm: state => state.newTemplateForm,
			newTemplateFormVisibility: state => state.newTemplateFormVisibility,
			noteHistory: state => {
				const { timezone, } = getUserProfile();
				const { subDocs, } = state;

				return computeNoteHistory( subDocs, {
					timezone,
					translate,
				} );
			},
			oneTimeMeetingForm: state => state.oneTimeMeetingForm,
			oneTimeMeetingFormVisibility: state => state.oneTimeMeetingFormVisibility,
			oneTimeMeetings: state => {
				const { oneTimeMeetings, customTemplates, } = state;
				return mapMeetings( { meetings: oneTimeMeetings, templates: customTemplates, } );
			},
			popoverSubDocId: state => state.popoverSubDocId,
			/** recommended templates */
			recommendedTemplates( state, getters ) {
				const {
					defaultTeamTypeSuggestedTemplates,
					teamTypeSuggestedTemplates,
					teamSpaceCustomTemplates,
				} = getters;

				return computeRecommendedTemplates(
					defaultTeamTypeSuggestedTemplates,
					teamTypeSuggestedTemplates,
					teamSpaceCustomTemplates
				);
			},
			recurrenceForm: state => state.recurrenceForm,
			recurrences: state => {
				const { customTemplates, recurrences, } = state;
				return mapMeetings( { meetings: recurrences, templates: customTemplates, } );
			},
			recurrenceFormVisibility: state => state.recurrenceFormVisibility,
			icsMeetingInstructionsVisible: state => state.icsMeetingInstructionsVisible,
			selectedSuggestedTopics: state => state.selectedSuggestedTopics,
			selectedTemplateCategory: state => state.selectedTemplateCategory,
			selectedTemplates: state => state.selectedTemplates,
			selectedTopicCategory: ( state, getters ) => {
				const { selectedTopicCategory, } = state;
				const { suggestedTopics, } = getters;
				return suggestedTopics.find( suggestion => suggestion.category === selectedTopicCategory ) || {
					...pristineTopicCategory( i18n ),
					category: selectedTopicCategory,
				};
			},
			selectedTranscript: state => {
				const { transcriptions, transcriptionViewedSubDocId, } = state;
				return transcriptions[ transcriptionViewedSubDocId ] ||
					pristineTranscription( transcriptionViewedSubDocId );
			},
			subDocCount: state => state.subDocs.length,
			subDocs: state => {
				const {
					subDocs: stateSubDocs,
					cursors,
					currentDocType,
					identifiedNote,
					transcriptionSummaries,
					transcriptionWizards,
					recurrenceInfos,
				} = state;

				const cursorSubDocId = get( cursors, [ currentDocType, "subDocId", ] );

				return stateSubDocs.map( subDoc => {
					const { subDocId, virtualMeetingUrl, } = subDoc;
					let autofocus = false;
					if ( cursorSubDocId === subDocId ) {
						autofocus = cursors[ currentDocType ].anchor;
					}

					const transcriptionSummary = transcriptionSummaries[ subDocId ] ||
						pristineTranscriptionSummary( subDocId );

					const recurrenceInfo = recurrenceInfos[ subDocId ] || pristineRecurrenceInfo( subDocId );

					const {
						status,
						subStatus,
						summary,
						itemizedSummary,
						rating,
						origin,
						transcriptionStatusCycle,
					} = transcriptionSummary;

					return {
						...subDoc,
						// creator is undefined for initial auto-injected notes
						creator: subDoc.creator || "",
						autofocus,
						// isIcsManaged: (subDoc.managedBy === 'ics'),
						isIdentified: ( identifiedNote === subDocId ),
						isAssistantEnabled: !!transcriptionWizards[ subDocId ],
						shortDate: subDoc.date.format( "YYYY-MM-DD" ),
						transcriptionUIKey: `${ subDocId }:${ status }:${ subStatus }:${ virtualMeetingUrl }`,
						transcriptionStatus: status,
						transcriptionSubStatus: subStatus,
						transcriptionSummary: summary,
						transcriptionItemizedSummary: itemizedSummary,
						transcriptionRating: rating,
						transcriptionOrigin: origin,
						transcriptionStatusCycle,
						canRecur: !!subDoc.virtualMeetingUrl,
						...recurrenceInfo,
						hasRecurrence: !!recurrenceInfo.recurrenceId,
					};
				} );
			},
			suggestedTemplates: state => {
				const userProfile = getUserProfile();
				const { selectedTemplates, suggestedTemplates, myTeams, } = state;
				if ( suggestedTemplates.length === 0 ) {
					return [];
				}

				const { personId: currentUserId, displayName: currentUserName, } = userProfile;

				const contextualizeTemplate = templateFactory( {
					// NOTE: templates can have translation keys from different namespaces,
					// so use the global translation function here
					translate: t,
					templates: suggestedTemplates,
				} );

				const contextualizedTemplates = templateOrder().map( templateType => {
					return contextualizeTemplate( {
						currentUserId,
						currentUserName,
						templateType,
					} );
				} );

				return extendTemplates( contextualizedTemplates, selectedTemplates, myTeams );
			},
			suggestedTopics: state => {
				const { suggestedTopics, selectedSuggestedTopics, } = state;
				return suggestedTopics.map( topicCategory => {
					return {
						...topicCategory,
						// eslint-disable-next-line no-magic-numbers
						primaryTopics: [ ...topicCategory.topics.slice( 0, 5 ), ].map( primaryTopic => {
							const lastAddedAt = selectedSuggestedTopics[ primaryTopic.id ];
							return {
								...primaryTopic,
								lastAdded: lastAddedAt ? moment.utc( lastAddedAt ).format( "M/D/YYYY" ) : "",
							};
						} ),
						// eslint-disable-next-line no-magic-numbers
						secondaryTopics: [ ...topicCategory.topics.slice( 5 ), ].map( secondaryTopic => {
							const lastAddedAt = selectedSuggestedTopics[ secondaryTopic.id ];
							return {
								...secondaryTopic,
								lastAdded: lastAddedAt ? moment.utc( lastAddedAt ).format( "MM/DD/YYYY" ) : "",
							};
						} ),
					};
				} );
			},
			teamSpaceCustomTemplates( state, getters ) {
				const { currentTeam, customTemplates, } = getters;
				return customTemplates.filter( template => template.category === currentTeam.id );
			},
			teamTelemetryData( state ) {
				const userProfile = getUserProfile();
				const { currentTeam, } = state;

				const {
					id: teamId = "",
					name: teamName = "",
					members = [],
					isTeamingTeam: isSampleTeam = false,
				} = currentTeam;

				const {
					personId: thisMemberId,
					displayName: thisMemberName,
				} = userProfile;

				const thisTeamMember = members.find( member => member.personId === thisMemberId );
				const { role: thisMemberRole, } = thisTeamMember;

				return {
					thisMemberId,
					thisMemberName,
					thisMemberRole,
					teamId,
					teamName,
					teamType: currentTeam.getTemplateType(),
					isSampleTeam,
				};
			},
			teamTemplateScope( state ) {
				const { currentTeam, } = state;
				return currentTeam.getTemplateScope();
			},
			teamTemplateType( state ) {
				const { currentTeam, } = state;
				return currentTeam.getTemplateType();
			},
			/** suggested templates filtered according to the current team type */
			teamTypeSuggestedTemplates( state, getters ) {
				const { currentTeam, } = state;
				const { suggestedTemplates, } = getters;
				const teamTemplateScope = currentTeam.getTemplateScope();
				return suggestedTemplates.filter( template => template.scope === teamTemplateScope );
			},
			templateBrowserCategories( state ) {
				const { templateTeamCategories, customTemplates, } = state;

				const teamingTemplatesGroup = {
					group: translate( "teamingTemplatesGroupLabel" ),
					options: [
						{
							text: translateTemplateCategory( TemplateCategory.TEAM_TEMPLATES ),
							value: TemplateCategory.TEAM_TEMPLATES,
							suffix: "",
							description: translate( `templateCategory${ TemplateCategory.TEAM_TEMPLATES }description` ),
							placeholder: translate( `templateCategory${ TemplateCategory.TEAM_TEMPLATES }placeholder` ),
						},
						{
							text: translateTemplateCategory( TemplateCategory.ONE_ON_ONE_TEMPLATES ),
							value: TemplateCategory.ONE_ON_ONE_TEMPLATES,
							suffix: "",
							description: translate( `templateCategory${ TemplateCategory.ONE_ON_ONE_TEMPLATES }description` ),
							placeholder: translate( `templateCategory${ TemplateCategory.ONE_ON_ONE_TEMPLATES }placeholder` ),
						},
					],
				};

				// filter custom template categories so that only MY_TEMPLATES and
				// teams with templates are displayed
				const categoriesWithTemplates = customTemplates.reduce( ( acc, template ) => {
					acc[ template.teamId ] = true;
					return acc;
				}, {
					[ TemplateCategory.MY_TEMPLATES ]: true,
				} );

				const customTemplatesGroup = {
					group: translate( "customTemplatesGroupLabel" ),
					options: templateTeamCategories
						.filter( category => !!categoriesWithTemplates[ category.value ] ),
				};

				return [
					teamingTemplatesGroup,
					customTemplatesGroup,
				];
			},
			templateBrowserVisibility: state => state.templateBrowserVisibility,
			templatePreview: state => state.templatePreview,
			templatePreviewVisibility: state => state.templatePreviewVisibility,
			topicBrowserVisibility: state => state.topicBrowserVisibility,
			topicCategories: state => {
				const { suggestedTopics, selectedTopicCategory, } = state;
				const categories = suggestedTopics.map( suggestion => {
					const { category, description, sortOrder, } = suggestion;
					const isSelected = ( selectedTopicCategory === category );
					return {
						category,
						description,
						sortKey: sortOrder,
						isSelected,
					};
				} );
				return orderBy( categories, [ "sortKey", ], [ "asc", ] );
			},
			userProfile: () => {
				return getUserProfile();
			},
		},
		mutations: {
			// TODO currently a no-op. Not sure if I'll need to implement this yet or not
			// updateCursors( state, { subDocId, anchor, } ) {
			// 	state.cursors[ state.currentDocType ] = { subDocId, anchor, };
			// },
			assistMeeting( state, { subDocId, bypassPrompt, } ) {
				state.isTranscriptionBotPromptVisible = !bypassPrompt;
				state.meetingAssistantSubDocId = subDocId;
			},
			browseTemplates( state, subDocId ) {
				state.popoverSubDocId = subDocId;
				state.templateBrowserVisibility = PopoverVisibility.VISIBLE;
			},
			browseTopics( state, subDocId ) {
				const { currentTeam, } = state;
				try {
					const teamTelemetry = getTeamTelemetry( currentTeam );
					teamTelemetry.openSuggestedTopicPanel();
				} catch ( e ) {
					console.error( "telemetry failure", e.message );
				}

				state.popoverSubDocId = subDocId;
				state.selectedTopicCategory = TopicCategory.ICEBREAKERS;
				state.topicBrowserVisibility = PopoverVisibility.VISIBLE;
			},
			clearPopoverSubDocId( state ) {
				state.popoverSubDocId = "";
			},
			closeAllPopovers( state ) {
				[
					"newTemplateFormVisibility",
					"oneTimeMeetingFormVisibility",
					"recurrenceFormVisibility",
					"recurrenceListVisibility",
					"templateBrowserVisibility",
					"templatePreviewVisibility",
					"topicBrowserVisibility",
				].forEach( prop => {
					if ( state[ prop ] === PopoverVisibility.VISIBLE ) {
						state[ prop ] = PopoverVisibility.HIDDEN;
					}
				} );

				state.transcriptionViewedSubDocId = "";
				state.isTranscriptionUploadPromptVisible = false;
				state.isTranscriptionBotPromptVisible = false;
				state.icsMeetingInstructionsVisible = false;
			},
			collapseNavigationSidebar( state ) {
				state.isNavigationSidebarExpanded = false;
				storage.collapseStreamNavigationSidebar();
			},
			editCustomTemplate( state, content ) {
				const { currentTeam, templateTeamCategories: categoryOptions, myTeams, } = state;
				const leadTeamIds = myLeadTeamIds( myTeams );

				const { id: teamId, } = currentTeam;
				const scope = currentTeam.getTemplateScope();
				const form = {
					teamId,
					content,
					scope,
				};
				const options = {
					leadTeamIds,
				};
				const readonlyData = {
					categoryOptions,
				};
				state.newTemplateForm = newTemplateForm( {
					priorForm: form,
					options,
					readonlyData,
					i18n,
				} );
				state.newTemplateFormVisibility = PopoverVisibility.VISIBLE;
			},
			ensureCustomTemplateFormValidation( state ) {
				const { newTemplateForm: form, templateTeamCategories: categoryOptions, myTeams, } = state;
				const leadTeamIds = myLeadTeamIds( myTeams );
				const options = {
					touchAll: true,
					leadTeamIds,
				};
				const readonlyData = {
					categoryOptions,
				};
				state.newTemplateForm = newTemplateForm( {
					priorForm: form,
					options,
					readonlyData,
					i18n,
				} );
			},
			ensureOneTimeMeetingFormValidation( state ) {
				const { timezone, } = getUserProfile();
				const { oneTimeMeetingForm: form, } = state;
				const options = { touchAll: true, };
				const readonlyData = { timezone, };
				state.oneTimeMeetingForm = newOneTimeMeetingForm( {
					priorForm: form,
					options,
					readonlyData,
					i18n,
				} );
			},
			ensureRecurrenceFormValidation( state ) {
				const { timezone, } = getUserProfile();
				const { recurrenceForm: form, } = state;
				const options = { touchAll: true, };
				const readonlyData = { timezone, };
				state.recurrenceForm = newRecurrenceForm( {
					priorForm: form,
					options,
					readonlyData,
					i18n,
				} );
			},
			expandNavigationSidebar( state ) {
				state.isNavigationSidebarExpanded = true;
				storage.expandStreamNavigationSidebar();
			},
			hideTranscription( state ) {
				state.transcriptionViewedSubDocId = "";
			},
			hideTranscriptionWizard( state, { subDocId, } ) {
				state.transcriptionWizards = Object.keys( state.transcriptionWizards )
					.filter( wizardsSubDocId => wizardsSubDocId !== subDocId )
					.reduce( ( acc, wizardSubDocId ) => {
						acc[ wizardSubDocId ] = true;
						return acc;
					}, {} );
			},
			identifyNote( state, { subDocId, } ) {
				state.identifiedNote = subDocId;
			},
			previewTemplate( state, template ) {
				state.templatePreview = template;
				state.templatePreviewVisibility = PopoverVisibility.VISIBLE;
			},
			receiveCustomTemplates( state, templates ) {
				state.customTemplates = templates.map( template => {
					return {
						...template,
						title: template.name,
						topics: flattenTemplateTopics( template ),
						isCustomTemplate: true,
					};
				} );
			},
			receiveOneTimeMeetings( state, { oneTimeMeetings, } ) {
				state.oneTimeMeetings = oneTimeMeetings;
			},
			receiveRecurrences( state, { recurrences, } ) {
				state.recurrences = recurrences;
			},
			receiveSelectedSuggestedTopics( state, { teamId, selectedSuggestedTopics, } ) {
				if ( teamId !== state.currentTeamId ) {
					return;
				}
				state.selectedSuggestedTopics = selectedSuggestedTopics;
			},
			receiveSelectedTemplates( state, { teamId, selectedTemplates, } ) {
				if ( teamId !== state.currentTeamId ) {
					return;
				}
				state.selectedTemplates = selectedTemplates;
			},
			receiveTranscriptionSummaries( state, transcriptionSummaries ) {
				const updatedSummaries = {
					...state.transcriptionSummaries,
				};

				for ( const transcriptionSummary of transcriptionSummaries ) {
					const { subDocId, } = transcriptionSummary;
					const existingSummary = updatedSummaries[ subDocId ] || pristineTranscriptionSummary( subDocId );
					const { transcriptionStatusCycle, } = existingSummary;
					updatedSummaries[ subDocId ] = {
						// NOTE: carry over UI-only related attributes unless they
						// are specifically overridden
						...transcriptionSummary,
						transcriptionStatusCycle: transcriptionStatusCycle + 1,
					};
				}

				state.transcriptionSummaries = updatedSummaries;
			},
			receiveRecurrenceInfos( state, infos ) {
				const updatedInfos = {
					...state.recurrenceInfos,
				};

				for ( const info of infos ) {
					const { subDocId, } = info;
					updatedInfos[ subDocId ] = info;
				}

				state.recurrenceInfos = updatedInfos;
			},
			resetStreamStoreState( state, {
				team,
				myTeams,
				docType,
				documentName,
				suggestedTopics,
				customTemplates,
				suggestedTemplates,
				documentSupplements,
				oneTimeMeetings,
				recurrences,
			} = {} ) {
				Object.assign( state, pristineState( i18n ) );

				state.isNavigationSidebarExpanded = storage.isStreamNavigationSidebarExpanded();

				const { timezone, } = getUserProfile();
				const readonlyData = {
					timezone,
				};
				state.recurrenceForm = newRecurrenceForm( { readonlyData, i18n, } );
				state.oneTimeMeetingForm = newOneTimeMeetingForm( { readonlyData, i18n, } );

				if ( suggestedTopics ) {
					state.suggestedTopics = suggestedTopics;
				}

				if ( customTemplates ) {
					state.customTemplates = customTemplates.map( template => {
						return {
							...template,
							title: template.name,
							topics: flattenTemplateTopics( template ),
							isCustomTemplate: true,
						};
					} );
				}

				if ( suggestedTemplates ) {
					state.suggestedTemplates = suggestedTemplates;
				}

				if ( team ) {
					state.currentTeamId = team.id;
					state.currentTeam = team;
					const { selectedSuggestedTopics = {}, selectedTemplates = {}, } = team.characteristics;
					state.selectedSuggestedTopics = selectedSuggestedTopics;
					state.selectedTemplates = selectedTemplates;
					state.selectedTemplateCategory = team.getTemplateCategory();
				}

				if ( myTeams ) {
					state.myTeams = myTeams;
				}

				if ( team && myTeams ) {
					const teamCategoryOptions = myTeams
						.map( myTeam => {
							const { id: teamId, extendedName: teamName, } = myTeam;
							const templateCategory = myTeam.getTemplateCategory();
							let categoryName = teamName;
							let suffix = "";
							let sortKey1, sortKey2;

							if ( myTeam.isOneOnOne() ) {
								suffix = "1:1";
							}

							if ( teamId === team.id ) {
								categoryName = translate( "thisSpaceCategoryName", {
									categoryName,
								} );
								// current team category should be first
								sortKey1 = 1;
								sortKey2 = "";
							} else {
								// all other team categories sorted by name
								sortKey1 = 2;
								sortKey2 = categoryName.toLowerCase();
							}

							return {
								text: categoryName,
								value: teamId,
								suffix, // suffix is used for 1:1s in desktop dialogs
								description: "", // no descriptions for teams
								placeholder: translate( `templateCategory${ templateCategory }placeholder` ),
								mark: markMap.get( myTeam.type ),
								sortKey1,
								sortKey2,
							};
						} );
					state.templateTeamCategories = [
						// "My Templates" category is included in the pristine state, by default,
						// and should always come first
						...state.templateTeamCategories,
						...orderBy( teamCategoryOptions, [ "sortKey1", "sortKey2", ], [ "asc", "asc", ] ),
					];
				}

				if ( docType ) {
					state.currentDocType = docType;
				}

				if ( documentName ) {
					state.documentName = documentName;
				}

				if ( documentSupplements ) {
					const summaries = {};
					const transcriptions = {};
					const recurrenceInfos = {};

					for ( const supplement of documentSupplements ) {
						const { subDocId, summary, transcription, recurrenceInfo } = supplement;
						const { status, } = summary;
						const isFailedTranscription = [
							TranscriptionJobStatus.FAILED,
							TranscriptionJobStatus.ABORTED,
						].includes( status );
						summaries[ subDocId ] = {
							...pristineTranscriptionSummary( subDocId ),
							// previously failed summaries should be "excluded" when the stream is loaded;
							// the user may retry them by kicking off a new transcription
							...( isFailedTranscription ? {} : summary ),
							subDocId,
						};
						transcriptions[ subDocId ] = {
							...pristineTranscription( subDocId ),
							speechBlocks: transcription,
						};
						recurrenceInfos[ subDocId ] = { ...pristineRecurrenceInfo( subDocId ), ...( recurrenceInfo || {} )};
					}

					state.transcriptionSummaries = summaries;
					state.transcriptions = transcriptions;
					state.recurrenceInfos = recurrenceInfos;
				}

				if ( oneTimeMeetings ) {
					state.oneTimeMeetings = oneTimeMeetings;
				}

				if ( recurrences ) {
					state.recurrences = recurrences;
				}

				if ( team && docType ) {
					state.isInitialized = true;
				}

				luxonext.nthWeekdayOfMonth.flushCache();
			},
			selectTemplateCategory( state, category ) {
				state.selectedTemplateCategory = category;
			},
			selectTopicCategory( state, category ) {
				state.selectedTopicCategory = category;
			},
			setBusy( state, flag ) {
				state.busy = {
					...state.busy,
					[ flag ]: true,
				};
			},
			setSubDocs( state, { subDocs: editorSubDocs, } ) {
				state.subDocSyncCycle += 1;

				const { subDocs: stateSubDocs, } = state;

				let update = true;

				// if subdocs have been added or removed, update the collection
				if ( editorSubDocs.length === stateSubDocs.length ) {
					// if any subdocs have *changed*, update the collection
					const storeSubDocMap = stateSubDocs.reduce( ( acc, subDoc ) => {
						const { subDocId, date, title, } = subDoc;
						acc[ subDocId ] = {
							subDocId,
							date: moment( date ).format( "YYYY-MM-DD HH:mm:ss" ),
							title,
						};
						return acc;
					}, {} );

					update = !!editorSubDocs.find( editorSubDoc => {
						const { subDocId, date, title, virtualMeetingUrl, } = editorSubDoc;
						const storeSubDoc = storeSubDocMap[ subDocId ];
						return !(
							moment.utc( date ).isSame( moment.utc( storeSubDoc.date ) ) &&
							title === storeSubDoc.title &&
							virtualMeetingUrl === storeSubDoc.virtualMeetingUrl
						);
					} );
				}

				if ( !update ) {
					debug( "no sub-doc changes; skipping store update" );
					return;
				}

				// need to map here so that the store does NOT hold
				// references to individual subDocs
				state.subDocs = orderBy(
					editorSubDocs.map( subDoc => {
						const { title, date, } = subDoc;
						return {
							...subDoc,
							title: ( title || defaultNoteTitle( subDoc ) ),
							date: moment.utc( date ),
						};
					} ),
					[ "date", ],
					[ "desc", ]
				);
			},
			showOneTimeMeetingForm( state, { recurrenceId = "", } = {} ) {
				if (!recurrenceId) {
					// only allow edit; new meetings should be managed via ics
					state.icsMeetingInstructionsVisible = true;
					return;
				}
				const { documentName, } = state;
				const { timezone, } = getUserProfile();
				const readonlyData = { timezone, };

				let fields = {
					documentName,
					recurrenceId,
				};
				const meeting = state.oneTimeMeetings.find( m => m.id === recurrenceId );
				fields = oneTimeMeetingToFields( meeting );
					state.oneTimeMeetingForm = newOneTimeMeetingForm( {
						priorForm: fields,
						readonlyData,
						i18n,
					} );
					state.oneTimeMeetingFormVisibility = PopoverVisibility.VISIBLE;
			},
			showRecurrenceForm( state, { recurrenceId = "", subDocId = "", } = {} ) {
				if (!recurrenceId) {
					// only allow edit; new meetings should be managed via ics
					state.icsMeetingInstructionsVisible = true;
					return;
				}
				const { documentName, subDocs, recurrences, } = state;
				const { timezone, } = getUserProfile();
				const readonlyData = { timezone, };

				let fields = {
					documentName,
					recurrenceId,
					subDocId,
				};

				if ( recurrenceId ) {
					// "editing" a specific recurrence
					const recurrence = recurrences.find( r => r.id === recurrenceId );
					fields = recurrenceToFields( recurrence );
				} else if ( subDocId ) {
					// assigning a new recurrence to a subDoc
					const subDoc = subDocs.find( s => s.subDocId === subDocId );
					fields.nameField = subDoc.title;
					fields.linkField = subDoc.virtualMeetingUrl;
				}
				state.recurrenceForm = newRecurrenceForm( {
					priorForm: fields,
					readonlyData,
					i18n,
				} );
				state.recurrenceFormVisibility = PopoverVisibility.VISIBLE;
			},
			showRecurrenceList( state ) {
				state.recurrenceListVisibility = PopoverVisibility.VISIBLE;
			},
			showTranscription( state, transcription ) {
				const { subDocId, } = transcription;
				state.transcriptions = {
					...state.transcriptions,
					[ subDocId ]: transcription,
				};
				state.transcriptionViewedSubDocId = subDocId;
			},
			showTranscriptionUploadPrompt( state ) {
				state.isTranscriptionUploadPromptVisible = true;
			},
			showTranscriptionWizard( state, { subDocId, } ) {
				state.transcriptionWizards = {
					...state.transcriptionWizards,
					[ subDocId ]: true,
				};
			},
			unassistMeeting( state ) {
				state.meetingAssistantSubDocId = "";
			},
			setDocType( state, docType ) {
				state.currentDocType = docType;
			},
			unsetBusy( state, flag ) {
				state.busy = {
					...state.busy,
					[ flag ]: false,
				};
			},
			updateCustomTemplateForm( state, form ) {
				const { templateTeamCategories: categoryOptions, myTeams, } = state;
				const leadTeamIds = myLeadTeamIds( myTeams );
				const options = {
					leadTeamIds,
				};
				const readonlyData = {
					categoryOptions,
				};
				state.newTemplateForm = newTemplateForm( {
					priorForm: form,
					options,
					readonlyData,
					i18n,
				} );
			},
			updateOneTimeMeetingForm( state, form ) {
				const { timezone, } = getUserProfile();
				const patchedForm = {
					...state.oneTimeMeetingForm,
					...form,
				};
				const readonlyData = { timezone, };
				state.oneTimeMeetingForm = newOneTimeMeetingForm( {
					priorForm: patchedForm,
					readonlyData,
					i18n,
				} );
			},
			updateRecurrenceForm( state, form ) {
				const { timezone, } = getUserProfile();
				const patchedForm = {
					...state.recurrenceForm,
					...form,
				};
				const readonlyData = { timezone, };
				state.recurrenceForm = newRecurrenceForm( {
					priorForm: patchedForm,
					readonlyData,
					i18n,
				} );
			},
		},
		actions: {
			async initialize( { commit, }, { teamId = "", docType = "", } ) {
				try {
					const userProfile = getUserProfile();
					const teamResponse = await ioTeam.fetchTeam( { teamId, } );
					const team = teamResponse.data;
					const isRegularTeam = ( team.type === TeamType.TEAM );

					const prefix = team.getDocumentNamePrefix();
					const documentName = ( docType === StreamDocumentType.SHARED ) ?
						`${ prefix }.${ team.id }.${ docType }` :
						`${ prefix }.${ team.id }.${ docType }.${ userProfile.personId }`;

					const [
						teamsResponse,
						suggestedTopics, // TODO: why isn't this a standard response?
						customTemplatesResponse,
						teamingTemplatesResponse,
						documentSupplementsResponse,
						recurrencesResponse,
					] = await Promise.all( [
						ioTeam.fetchAllMyTeams( { limit: Number.MAX_SAFE_INTEGER, } ),
						ioTeam.fetchSuggestedTopics(),
						ioMeeting.fetchCustomTemplates(),
						ioMeeting.fetchTeamingTemplates(),
						ioStream.fetchDocumentSupplements( { documentName, }, { withSummaries: true, } ),
						ioStream.fetchRecurrences( { documentName, } ),
					] );
					// recurrencesResponse.data contains both recurrences and one-time meetings
					// filter these based on isOneTime flag
					const recurrences = recurrencesResponse.data.filter( recurrence => !recurrence.isOneTime );
					const oneTimeMeetings = recurrencesResponse.data.filter( recurrence => recurrence.isOneTime );

					const filteredSuggestedTopics = isRegularTeam ?
						suggestedTopics.filter( topic => topic.category === TopicCategory.ICEBREAKERS ) :
						suggestedTopics;

					commit( "resetStreamStoreState", {
						team,
						myTeams: teamsResponse.data,
						docType,
						documentName,
						suggestedTopics: filteredSuggestedTopics,
						customTemplates: customTemplatesResponse.data,
						suggestedTemplates: teamingTemplatesResponse.data,
						documentSupplements: documentSupplementsResponse.data.supplements,
						oneTimeMeetings,
						recurrences,
					} );
				} catch ( e ) {
					error( "unexpectedError", e );
				}

				try {
					// TODO: add dedicated endpoint to record template usage
					ioTeam.saveTeamCharacteristics( {
						teamId,
						characteristics: {
							templateUsageMigrationAttempted: true,
						},
					} );
				} catch ( e ) {
					console.error( "unable to migrate templates", e );
				}
			},
			async recordNoteCreation( { state, getters, }, { title, date, link, } ) {
				try {
					const { currentTeam, } = state;
					const { teamTelemetryData, } = getters;
					const { thisMemberId: initiatorId, thisMemberName: initiatorName, } = teamTelemetryData;

					const telemetryArgs = {
						...teamTelemetryData,
						initiatorId,
						initiatorName,
						isThisMemberInitiator: true,
						noteTimestamp: date,
						noteName: title,
						noteDeepLink: link,
					};

					const teamTelemetry = getTeamTelemetry( currentTeam );
					teamTelemetry.streamNoteCreated( telemetryArgs );
				} catch ( e ) {
					console.error( "telemetry failure", e.message );
				}
			},
			async recordTemplateUsage( { state, getters, commit, }, { template, title, link, } ) {
				const { currentTeam, } = state;
				const { teamTelemetryData, } = getters;
				const { characteristics = {}, } = currentTeam;
				const { selectedTemplates: teamSelectedTemplates = {}, } = characteristics;
				const { meetingTemplateId, } = template;

				try {
					const selectedTemplates = {
						[ meetingTemplateId ]: templateSelectionTimestamp.create(),
					};

					// pre-emptive commit
					commit( "receiveSelectedTemplates", {
						...teamSelectedTemplates,
						...selectedTemplates,
					} );

					// TODO: add dedicated endpoint to record template usage
					await ioTeam.saveTeamCharacteristics( {
						teamId: currentTeam.id,
						characteristics: {
							selectedTemplates,
						},
					} );
				} catch ( e ) {
					console.error( "unable to update team characteristics", e );

					// revert pre-emptive commit
					return commit( "receiveSelectedTemplates", {
						...teamSelectedTemplates,
					} );
				}

				try {
					const teamTelemetry = getTeamTelemetry( currentTeam );
					teamTelemetry.addMeetingTemplate( { templateTitle: template.title, } );
					teamTelemetry.streamNoteTemplateSelected( {
						...teamTelemetryData,
						templateName: template.title,
						templateType: this.teamTemplateType,
						noteName: title,
						noteDeepLink: link,
					} );
				} catch ( e ) {
					console.error( "telemetry failure", e.message );
				}
			},
			async recordTopicUsage( { state, commit, }, { topic, } ) {
				const { currentTeam, selectedTopicCategory, } = state;
				const { characteristics = {}, } = currentTeam;
				const { selectedSuggestedTopics: teamSelectedSuggestedTopics = {}, } = characteristics;

				try {
					const selectedSuggestedTopics = {
						[ topic.id ]: templateSelectionTimestamp.create(),
					};

					// pre-emptive commit
					commit( "receiveSelectedSuggestedTopics", {
						...teamSelectedSuggestedTopics,
						...selectedSuggestedTopics,
					} );

					await ioTeam.saveTeamCharacteristics( {
						teamId: currentTeam.id,
						characteristics: {
							selectedSuggestedTopics,
						},
					} );
				} catch ( e ) {
					console.error( "unable to update team characteristics", e );

					// revert pre-emptive commit
					return commit( "receiveSelectedSuggestedTopics", {
						...teamSelectedSuggestedTopics,
					} );
				}

				try {
					const teamTelemetry = getTeamTelemetry( currentTeam );
					teamTelemetry.addSuggestedTopic( {
						categoryName: selectedTopicCategory,
						topicText: topic.text,
					} );
				} catch ( e ) {
					console.error( "telemetry failure", e.message );
				}
			},
			async tryAbortTranscriptionBot( { commit, getters, state, }, { subDocId, } ) {
				try {
					const { documentName, } = getters;
					const { transcriptionSummaries, } = state;
					const transcriptionSummary = transcriptionSummaries[ subDocId ];
					const transcriptionJobId = transcriptionSummary ? transcriptionSummary.id : null;

					// abort the HTTP requests
					const retryRequest = transcriptionBotRetries[ subDocId ];
					if ( retryRequest ) {
						retryRequest.abort();
						transcriptionBotRetries[ subDocId ] = null;
						delete transcriptionBotRetries[ subDocId ];
					}

					if ( !transcriptionJobId ) {
						return;
					}

					commit( "receiveTranscriptionSummaries", [ {
						...pristineTranscriptionSummary( subDocId ),
						status: TranscriptionJobStatus.ABORTED,
						origin: TranscriptionOrigin.BOT,
					}, ] );

					await ioStream.abortTranscription( { documentName, subDocId, transcriptionJobId, } );

					success( "abortTranscriptionSuccess" );
				} catch ( e ) {
					error( "abortTranscriptionError", e );
				}
			},
			tryAuthorizeZoom() {
				window.name = v4();  // unique, temp window name for redirect
				storage.storeRedirectToUrl( navigation.getLocationHref() );
				storage.storeRedirectToTarget( window.name );
				window.open( generateZoomAuthUrl( {
					clientId: config.zoomMeetingSdkClientId,
					redirectUri: config.zoomOauthRedirectUri,
					zoomOauthUrl: config.zoomOauthUrl,
				} ) );
			},
			async tryDeleteCustomTemplate( { commit, getters, }, { template, } ) {
				const {
					currentTeam,
				} = getters;

				const { meetingTemplateId, name, } = template;

				try {
					await ioMeeting.deleteCustomTemplate( { meetingTemplateId, } );

					commit( "closeAllPopovers" );
					success( "deleteCustomTemplateSuccess" );
				} catch ( e ) {
					return error( "deleteCustomTemplateError", e );
				}

				try {
					const customTemplatesResponse = await ioMeeting.fetchCustomTemplates();
					commit( "receiveCustomTemplates", customTemplatesResponse.data );
				} catch ( e ) {
					error( "fetchCustomTemplatesError", e );
				}

				try {
					const teamTelemetry = getTeamTelemetry( currentTeam );
					teamTelemetry.deletedCustomTemplate( { template: name, } );
				} catch ( e ) {
					console.error( "telemetry failure", e.message );
				}
			},
			async tryDeleteRecurrence( { state, commit, }, { recurrenceId, } ) {
				const { documentName, recurrences: staleRecurrences, oneTimeMeetings: staleOneTimeMeetings, } = state;
				const prunedOneTimeMeetings = staleOneTimeMeetings.filter( meeting => meeting.id !== recurrenceId );
				const prunedRecurrences = staleRecurrences.filter( recurrence => recurrence.id !== recurrenceId );
				try {
					commit( "receiveRecurrences", { recurrences: prunedRecurrences, } );
					commit( "receiveOneTimeMeetings", { oneTimeMeetings: prunedOneTimeMeetings, } );
					await ioStream.deleteRecurrence( { documentName, recurrenceId, } );

					commit( "closeAllPopovers" );
					success( "deleteRecurrenceSuccess" );
				} catch ( e ) {
					commit( "receiveRecurrences", { recurrences: [ ...staleRecurrences, ], } );
					commit( "receiveOneTimeMeetings", { oneTimeMeetings: [ ...staleOneTimeMeetings, ], } );
					error( "deleteRecurrenceError", e );
				}
			},
			async tryDeployTranscriptionBot( { commit, getters, }, { subDocId, virtualMeetingUrl, overrides = {} } ) {
				const { documentName, subDocs, } = getters;
				try {
					const { title: subDocTitle, managedBy } = subDocs.find( subDoc => subDoc.subDocId === subDocId );
					const subDocLink = streamNoteLink( subDocId );

					// add a placeholder summary, so we can show the alert notification prior
					// to getting the official websocket message that the request has been
					// created
					commit( "receiveTranscriptionSummaries", [ {
						...pristineTranscriptionSummary( subDocId ),
						status: TranscriptionJobStatus.REQUESTED,
						origin: TranscriptionOrigin.BOT,
					}, ] );
					const retryRequest = await ioStream.tryDeployTranscriptionBot( {
						documentName,
						subDocId,
						subDocTitle,
						subDocLink,
						managedBy: managedBy || 'ui',
						meetingURL: virtualMeetingUrl,
						...overrides,
					} );
					transcriptionBotRetries[ subDocId ] = retryRequest;

					const clearRetryRequest = () => {
						retryRequest.removeAllListeners();
						transcriptionBotRetries[ subDocId ] = null;
						delete transcriptionBotRetries[ subDocId ];
					};

					const commitError = status => {
						commit( "receiveTranscriptionSummaries", [ {
							...pristineTranscriptionSummary( subDocId ),
							status,
							origin: TranscriptionOrigin.BOT,
						}, ] );
						commit( "showTranscriptionWizard", { subDocId, } );
					};

					retryRequest.on( "exhaust", ( { attempt, status, } ) => {
						debug( `failed to deploy transcription bot (attempt: ${ attempt }, last status: ${ status })` );
						clearRetryRequest();
						commitError( TranscriptionJobStatus.FAILED );
					} );

					retryRequest.on( "failure", ( { attempt, status, } ) => {
						debug( `failed to deploy transcription bot (attempt: ${ attempt }, last status: ${ status })` );
						clearRetryRequest();
						commitError( TranscriptionJobStatus.FAILED );
					} );

					retryRequest.on( "aborting", () => {
						clearRetryRequest();
						commitError( TranscriptionJobStatus.ABORTED );
					} );

					retryRequest.on( "queue", ( { attempt, status, waitPeriodSeconds, } ) => {
						debug( `queueing transcription bot (attempt: ${ attempt }, last status: ${ status }, retry in: ${ waitPeriodSeconds } seconds)` );
						info( "botQueueMessage", { seconds: waitPeriodSeconds, } );
					} );

					retryRequest.on( "attempt", ( { attempt, } ) => {
						debug( `attempting to deploy transcription bot (attempt: ${ attempt })` );
					} );

					await retryRequest.promise();
				} catch ( e ) {
					error( "botErrorMessage", e );
				}
			},
			async tryRateTranscription( { commit, state, getters, }, { subDocId, rating, } ) {
				const { currentTeam, userProfile, documentName, } = getters;
				const { id: teamId, type: teamType, } = currentTeam;

				const { transcriptionSummaries, } = state;
				const transcriptionSummary = transcriptionSummaries[ subDocId ];
				const { id: transcriptionJobId, } = transcriptionSummary;

				try {
					const preUpdatedSummary = {
						...transcriptionSummary,
						rating,
					};

					commit( "receiveTranscriptionSummaries", [ preUpdatedSummary, ] );

					await ioStream.rateTranscription( { documentName, subDocId, transcriptionJobId, rating, } );
				} catch ( e ) {
					const revertedSummary = { ...transcriptionSummary, };
					commit( "receiveTranscriptionSummaries", [ revertedSummary, ] );

					return error( "rateTranscriptionError", e );
				}

				try {
					const { displayName: submitterName, } = userProfile;
					const teamTelemetry = getTeamTelemetry( currentTeam );
					teamTelemetry.streamNoteSummaryFeedbackSubmitted( {
						submitterName,
						teamId,
						teamType,
						subDocId,
						transcriptionJobId,
						rating,
					} );
				} catch ( e ) {
					console.error( "telemetry failure", e.message );
				}
			},
			async trySaveEditedTemplate( { commit, getters, } ) {
				const {
					currentTeam,
					newTemplateForm: form,
					newMaterializedTemplate,
				} = getters;

				if ( !form.allFieldsValid ) {
					return warn( "attentionNeededWarning" );
				}

				try {
					if ( newMaterializedTemplate.meetingTemplateId ) {
						// TODO: rework once we implement update feature again
						// await ioMeeting.updateCustomTemplate( newMaterializedTemplate );
					} else {
						await ioMeeting.createCustomTemplate( newMaterializedTemplate );
					}

					success( "templateSavedSuccess" );
					commit( "closeAllPopovers" );
				} catch ( e ) {
					return error( "saveCustomTemplateError", e );
				}

				try {
					const customTemplatesResponse = await ioMeeting.fetchCustomTemplates();
					commit( "receiveCustomTemplates", customTemplatesResponse.data );
				} catch ( e ) {
					return error( "fetchCustomTemplatesError", e );
				}

				try {
					if ( !form.meetingTemplateId ) {
						const teamTelemetry = getTeamTelemetry( currentTeam );
						teamTelemetry.createdCustomTemplate( { template: form.name, } );
					}
				} catch ( e ) {
					console.error( "telemetry failure", e.message );
				}
			},
			async trySaveOneTimeMeeting( { commit, state, } ) {
				const { oneTimeMeetingForm: form, } = state;

				if ( !form.allFieldsValid ) {
					warn( "attentionNeededWarning" );
					return commit( "unsetBusy", "trySaveOneTimeMeeting" );
				}

				try {
					const oneTimeMeeting = oneTimeMeetingFromFields( form );
					debug( { form, oneTimeMeeting, } );
					await ioStream.saveRecurrence( { recurrence: oneTimeMeeting, } );
					success( "oneTimeMeetingSavedSuccess" );
					commit( "closeAllPopovers" );
				} catch ( e ) {
					error( "saveOneTimeMeetingError", { message: e.message, } );
				} finally {
					commit( "unsetBusy", "trySaveOneTimeMeeting" );
				}
			},
			async trySaveRecurrence( { state, commit, } ) {
				const { recurrenceForm: form, } = state;

				if ( !form.allFieldsValid ) {
					warn( "attentionNeededWarning" );
					return commit( "unsetBusy", "trySaveRecurrence" );
				}

				try {
					const recurrence = recurrenceFromFields( form );
					debug( { form, recurrence, } );
					await ioStream.saveRecurrence( { recurrence, } );

					success( "recurrenceSavedSuccess" );
					commit( "closeAllPopovers" );
				} catch ( e ) {
					error( "saveRecurrenceError", { message: e.message, } );
				} finally {
					commit( "unsetBusy", "trySaveRecurrence" );
				}
			},
			async trySaveSettings( _, settings ) {
				try {
					const { settings: staleSettings, } = getUserProfile();
					const mergedSettings = {
						...staleSettings,
						...settings,
					};
					await ioUser.updateSettings( mergedSettings );
				} catch ( e ) {
					error( "saveSettingsError", e );
				}
			},
			async trySupplementNote( { getters, }, { subDocId, date, enableAssistant, } ) {
				try {
					const { documentName, } = getters;
					await ioStream.supplementSubDoc( { documentName, subDocId, date, enableAssistant, } );
				} catch ( e ) {
					console.error( "sub-doc supplement creation failure", e );
				}
			},
			async trySyncStream( { getters, }, { subDocId, cause = "", } ) {
				const {
					currentTeam,
					documentName,
				} = getters;

				if ( !( currentTeam && documentName ) ) {
					return debug( "attempted to sync stream, but missing something:", { currentTeam, documentName, } );
				}

				try {
					await ioStream.sendStreamNoteSyncNotice( {
						documentName,
						subDocId,
					} );

					if ( cause === "delete" ) {
						success( "deleteNoteSuccess" );
					}
				} catch ( e ) {
					error( "syncStreamError", e );
				}
			},
			async tryTranscribeUpload( { commit, getters, }, { subDocId, formFile, } ) {
				const { documentName, subDocs, } = getters;
				try {
					const { title: subDocTitle, } = subDocs.find( subDoc => subDoc.subDocId === subDocId );
					const subDocLink = streamNoteLink( subDocId );

					// add a placeholder summary so we can show the alert notification prior
					// to getting the official websocket message that the request has been
					// created
					commit( "receiveTranscriptionSummaries", [ {
						...pristineTranscriptionSummary( subDocId ),
						status: TranscriptionJobStatus.REQUESTED,
						origin: TranscriptionOrigin.UPLOAD,
					}, ] );

					ioStream.startTranscription( {
						documentName,
						subDocId,
						subDocTitle,
						subDocLink,
						formFile,
					} );
				} catch ( err ) {
					console.error( err );

					commit( "receiveTranscriptionSummaries", [ {
						...pristineTranscriptionSummary( subDocId ),
						status: TranscriptionJobStatus.FAILED,
						origin: TranscriptionOrigin.UPLOAD,
					}, ] );
				}
			},
			async tryViewTranscription( { commit, state, getters, }, { subDocId, } ) {
				const { documentName, } = getters;

				try {
					// if the transcription has already been loaded, use the cached version
					const existingTranscription = state.transcriptions[ subDocId ];
					if ( existingTranscription && existingTranscription.speechBlocks.length ) {
						return commit( "showTranscription", { ...existingTranscription, } );
					}

					// use an empty "placeholder" transcription object so the popup will display a "loading" indicator
					// while the real transcription is being fetched
					commit( "showTranscription", pristineTranscription( subDocId ) );

					const { data: transcription, } = await ioStream.fetchTranscription( { documentName, subDocId, } );

					commit( "showTranscription", transcription );
				} catch ( e ) {
					error( "viewTranscriptionError", e );
				}
			},
		},
	};
}
